import { useEffect, useMemo, useState } from 'preact/hooks';
import { generateBoard } from '../engine';
import { DEFAULT_SETTINGS, MAX_NAME_LENGTH, MAX_SEATS, MIN_PLAYERS_TO_START, type GameSettings } from '../shared/protocol';
import { Board } from './board';
import type { GameClient } from './game-client';
import { ChatPanel } from './chat';
import { SeatColors } from './colors';
import { PushToggle, usePush } from './push';
import { GameScreen } from './game-screen';
import { useRoom, type RoomControls, type RoomState } from './room';
import { uniqueSillyName } from '../shared/names';
import { BOT_LABELS, PLAYER_PALETTE, paletteColor } from './theme';
import { BotPicker, ColorPicker, SettingsForm, beginnerAvailable, selectOnFocus } from './widgets';

const NAME_KEY = 'hexagon.name';
const AUTOJOIN_KEY = 'hexagon.autojoin';
const PREVIEW_BOARD = generateBoard('fixed');

/** Marks a room the creator should be seated in as soon as its lobby connects. */
export function requestAutoJoin(code: string): void {
  try {
    sessionStorage.setItem(AUTOJOIN_KEY, code);
  } catch {
    // Storage blocked: the creator just joins by hand.
  }
}

function takeAutoJoin(code: string): boolean {
  try {
    if (sessionStorage.getItem(AUTOJOIN_KEY) !== code) return false;
    sessionStorage.removeItem(AUTOJOIN_KEY);
    return true;
  } catch {
    return false;
  }
}

export function readName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? '';
  } catch {
    return '';
  }
}

export function saveName(name: string): void {
  try {
    localStorage.setItem(NAME_KEY, name);
  } catch {
    // Private mode; the name just won't be remembered.
  }
}

function ConnectionBanner({ status }: { status: RoomState['status'] }) {
  if (status === 'open') return null;
  const text = status === 'connecting' ? 'Connecting…' : status === 'reconnecting' ? 'Reconnecting…' : 'Disconnected.';
  return (
    <div class="banner" data-testid="connection">
      {text}
    </div>
  );
}

function ShareLink({ code }: { code: string }) {
  const url = `${location.origin}/r/${code}`;
  const [copied, setCopied] = useState(false);
  return (
    <div class="row share">
      <span class="muted" data-testid="share-link">
        {url}
      </span>
      <button
        type="button"
        class="small secondary"
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
          } catch {
            // Clipboard blocked (e.g. insecure context); the link is still selectable.
          }
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function Lobby({ code, room, push }: { code: string; room: RoomState & RoomControls; push: ReturnType<typeof usePush> }) {
  const seats = room.room?.seats ?? [];
  const [name, setName] = useState(() => readName() || uniqueSillyName(seats.map((s) => s.name)));
  const isHost = room.seat !== null && room.seat === room.room?.host;
  const anyCpu = seats.some((s) => s.cpu);
  // Wait for the first room snapshot (it follows `welcome`) so a returning seat isn't asked to join twice.
  const ready = room.status === 'open' && room.room !== null;
  useEffect(() => {
    if (ready && room.seat === null && name.trim() && takeAutoJoin(code)) room.join(name.trim());
  }, [ready]);
  // The room's copy is the source of truth so every player sees the host's choices live.
  // The host's own edits show at once (and Start sends them) without waiting for the echo.
  const shared: GameSettings = room.room?.settings ?? DEFAULT_SETTINGS;
  const [draft, setDraft] = useState<GameSettings | null>(null);
  useEffect(() => setDraft(null), [JSON.stringify(shared)]);
  const settings = draft ?? shared;
  const startSettings: GameSettings = beginnerAvailable(seats.length, settings)
    ? settings
    : { ...settings, layout: 'random' };

  return (
    <SeatColors.Provider value={seats.map((x) => paletteColor(x.color))}>
      <Board board={PREVIEW_BOARD} robber={PREVIEW_BOARD.tiles.findIndex((t) => t.terrain === 'desert')} />
      <div class="panel">
        <h2>Room {code}</h2>
        <p class="muted">Send this link to everyone who's playing:</p>
        <ShareLink code={code} />
        <ol class="players" data-testid="players">
          {seats.map((s, i) => (
            <li key={i}>
              {i === room.seat || (s.cpu && isHost) ? (
                <ColorPicker
                  value={s.color}
                  taken={seats.filter((_, j) => j !== i).map((x) => x.color)}
                  onChange={(c) => room.setColor(c, i === room.seat ? undefined : i)}
                  testid={`color-${i}`}
                  label={`Colour for ${s.name}`}
                />
              ) : (
                <span class="swatch big" style={{ background: paletteColor(s.color) }} title={PLAYER_PALETTE[s.color]?.name} />
              )}
              {anyCpu && (
                <span class="tag-slot">
                  {s.cpu && (
                    <span class="cpu-tag" title={`${BOT_LABELS[s.bot ?? 'basic'].label} CPU: ${BOT_LABELS[s.bot ?? 'basic'].detail}`}>
                      CPU
                    </span>
                  )}
                </span>
              )}
              <span class="seat-name">{s.name}</span>
              {s.cpu && !isHost && <span class="muted small"> {BOT_LABELS[s.bot ?? 'basic'].label}</span>}
              {s.cpu && isHost && (
                <BotPicker value={s.bot ?? 'basic'} testid={`cpu-kind-${i}`} label={`Which CPU plays ${s.name}`} onChange={(k) => room.setCpu(i, k)} />
              )}
              {i === room.room?.host && <span class="muted"> (host)</span>}
              {i === room.seat && <span class="muted"> (you)</span>}
              {!s.connected && <span class="offline"> (away)</span>}
              {(!s.connected || s.cpu) && isHost && (
                <button class="small secondary remove-seat" data-testid={`remove-${i}`} aria-label={`Remove ${s.name}`} title={`Remove ${s.name}`} onClick={() => room.release(i)}>
                  ×
                </button>
              )}
            </li>
          ))}
        </ol>
        {room.seat === null ? (
          seats.length < MAX_SEATS ? (
            <form
              class="row"
              onSubmit={(e) => {
                e.preventDefault();
                if (!name.trim()) return;
                saveName(name.trim());
                room.join(name.trim());
              }}
            >
              <input
                value={name}
                onInput={(e) => setName((e.target as HTMLInputElement).value)}
                maxLength={MAX_NAME_LENGTH}
                placeholder="Your name"
                aria-label="Your name"
                data-testid="name-input"
                onFocus={selectOnFocus}
                autoFocus
              />
              <button type="submit" data-testid="join-room">
                Join
              </button>
            </form>
          ) : (
            <p class="muted">This room is full.</p>
          )
        ) : (
          <div class="row between wrap">
            {isHost && (
              <button
                type="button"
                class="secondary"
                data-testid="add-cpu"
                disabled={seats.length >= MAX_SEATS}
                onClick={() => room.addCpu()}
              >
                Add CPU player
              </button>
            )}
            {isHost ? (
              <button
                class="primary"
                data-testid="start-game"
                disabled={seats.length < MIN_PLAYERS_TO_START}
                title={seats.length < MIN_PLAYERS_TO_START ? `Waiting for at least ${MIN_PLAYERS_TO_START} players` : undefined}
                onClick={() => room.start(startSettings)}
              >
                Start game
              </button>
            ) : (
              <span class="muted" data-testid="waiting-host">
                Waiting for {seats[room.room?.host ?? 0]?.name ?? 'the host'} to start…
              </span>
            )}
            <button class="link" data-testid="leave-room" onClick={() => room.leave()}>
              Leave
            </button>
          </div>
        )}
        <SettingsForm
          value={settings}
          playerCount={Math.max(seats.length, MIN_PLAYERS_TO_START)}
          seatNames={seats.map((s) => s.name)}
          readOnly={!isHost}
          onChange={(next) => {
            setDraft(next);
            room.configure(next);
          }}
        />
        {!isHost && <p class="muted small">Only the host can change these.</p>}
        {room.seat !== null && <PushToggle push={push} />}
        {room.notice && <p class="error">{room.notice.text}</p>}
      </div>
      <ChatPanel messages={room.chat} seat={room.seat} send={room.say} />
    </SeatColors.Provider>
  );
}

/**
 * Mid-game seat handoff: the host can open an away player's seat, and anyone
 * watching can take an open one (e.g. the same person on a different device).
 */
function SeatsPanel({ room }: { room: RoomState & RoomControls }) {
  const seats = room.room?.seats ?? [];
  const isHost = room.seat !== null && room.seat === room.room?.host;
  const away = isHost ? seats.flatMap((s, i) => (!s.connected && !s.open ? [i] : [])) : [];
  const open = room.seat === null ? seats.flatMap((s, i) => (s.open ? [i] : [])) : [];
  const bots = isHost ? seats.flatMap((s, i) => (s.cpu ? [i] : [])) : [];
  if (away.length === 0 && open.length === 0 && bots.length === 0) return null;
  return (
    <div class="panel seats-panel" data-testid="seats-panel">
      {away.map((i) => (
        <div class="row between" key={`away-${i}`}>
          <span>
            <span class="swatch" style={{ background: paletteColor(seats[i]!.color) }} />
            {seats[i]!.name} is away.
          </span>
          <button class="small secondary" data-testid={`cpu-takeover-${i}`} onClick={() => room.cpuTakeover(i)}>
            Let the CPU play
          </button>
          <button
            class="small secondary"
            data-testid={`release-${i}`}
            onClick={() => {
              if (confirm(`Let someone else take ${seats[i]!.name}'s seat? Their current device will lose it.`)) room.release(i);
            }}
          >
            Open seat
          </button>
        </div>
      ))}
      {bots.map((i) => (
        <div class="row between" key={`cpu-${i}`}>
          <span>
            <span class="swatch" style={{ background: paletteColor(seats[i]!.color) }} />
            {seats[i]!.name} is played by the CPU.
          </span>
          <button class="small secondary" data-testid={`release-${i}`} onClick={() => room.release(i)}>
            Hand to a person
          </button>
        </div>
      ))}
      {open.map((i) => (
        <div class="row between" key={`open-${i}`}>
          <span>
            <span class="swatch" style={{ background: paletteColor(seats[i]!.color) }} />
            {seats[i]!.name}'s seat is open.
          </span>
          <button class="small primary" data-testid={`claim-${i}`} onClick={() => room.claim(i)}>
            Play as {seats[i]!.name}
          </button>
        </div>
      ))}
    </div>
  );
}

export function OnlineRoom({ code }: { code: string }) {
  const room = useRoom(code);
  const push = usePush(room.seat !== null && room.status === 'open', room.pushSubscribe, room.pushUnsubscribe);
  const { game } = room;
  const names = room.room?.seats.map((s) => s.name) ?? [];

  const client = useMemo<GameClient | null>(
    () =>
      game && {
        view: game.view,
        seat: room.seat,
        legal: game.legal,
        names,
        log: room.log,
        act: (action) => {
          room.act(action);
          return null;
        },
        hotSeat: false,
        connected: room.room?.seats.map((s) => s.connected),
        notice: room.notice,
        info: room.info,
        pending: room.pending,
        chat: { messages: room.chat, seat: room.seat, send: room.say },
        cpu: room.room?.seats.map((s) => s.cpu),
        bots: room.room?.seats.map((s) => s.bot),
        colors: room.room?.seats.map((s) => s.color),
        push,
        turnSeconds: room.room?.turnSeconds ?? null,
        ...(room.seat !== null && room.seat === room.room?.host
          ? {
              undo: room.undo,
              continueTo: room.continueTo,
              rematch: () => room.start(room.room?.settings ?? DEFAULT_SETTINGS),
              backToLobby: room.backToLobby,
            }
          : {}),
      },
    [game, room.seat, room.room, room.log, room.notice, room.info, room.pending, room.chat, push.status],
  );

  // A marker in the tab title so a backgrounded tab shows when you're up.
  const yourMove = !!game && game.legal.length > 0 && game.view.phase.kind !== 'gameOver';
  useEffect(() => {
    document.title = yourMove ? '● Your move — Hexagon' : 'Hexagon';
    return () => {
      document.title = 'Hexagon';
    };
  }, [yourMove]);

  if (room.fatal) {
    return (
      <div class="panel" data-testid="fatal">
        <h2>{room.fatal === 'room not found' ? 'Room not found' : 'Something went wrong'}</h2>
        <p class="muted">
          {room.fatal === 'room not found' ? `There's no room ${code}. It may have expired.` : room.fatal}
        </p>
        <a class="button" href="/">
          Back to start
        </a>
      </div>
    );
  }

  return (
    <>
      <ConnectionBanner status={room.status} />
      {client && room.room?.status !== 'lobby' ? (
        <>
          <SeatsPanel room={room} />
          <GameScreen client={client} />
        </>
      ) : (
        <Lobby code={code} room={room} push={push} />
      )}
    </>
  );
}
